import { useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Box, Typography, Tabs, Tab, useTheme, alpha } from "@mui/material";
import { PieChart as PieIcon, BarChart as BarIcon } from "@mui/icons-material";
import { useResponsive } from "../../../core/hooks/useResponsive";

// Matches the existing tooltip formatting (browser-default grouping).
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString()}`;

/**
 * Compact axis ticks. The previous formatter was `₹${(v / 1000).toFixed(0)}k`, which
 * printed "₹0k" for every value below 500 and "₹1k" for anything between 500 and 1499 —
 * so a small-budget run had an axis of identical labels.
 */
const compactInr = (v) => {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${Math.round(n)}`;
};

const ChartShell = ({ icon: Icon, title, subtitle, children, framed = true }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      sx={
        framed
          ? {
              borderRadius: 3,
              border: `1px solid ${alpha(primary, 0.12)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              p: 1.25,
            }
          : { p: 0 }
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <Icon sx={{ color: primary, fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  );
};

/**
 * Legend that carries the value next to each series name.
 *
 * The default legend only names the slices, so the counts were reachable only by
 * hovering — which does not exist on a touch device. Now the numbers are always on
 * screen and the tooltip is a bonus rather than the only way in.
 */
const PieValueLegend = ({ payload, data = [] }) => {
  // Look the count up in our own series rather than in entry.payload: recharts wraps
  // the datum differently across versions, and a silently-missing number is exactly
  // what this legend exists to prevent.
  const byName = new Map(data.map((d) => [d.name, d]));
  return (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      columnGap: 1.5,
      rowGap: 0.5,
      px: 0.5,
    }}
  >
    {(payload || []).map((entry) => {
      const datum = byName.get(entry.value) || {};
      return (
        <Box key={entry.value} sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: entry.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>
            {entry.value}
          </Typography>
          {!datum.hideValue && (
            <Typography variant="caption" sx={{ fontWeight: 800, color: entry.color, whiteSpace: "nowrap" }}>
              {datum.value}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
  );
};

const BTCharts = ({ tradeStatistics }) => {
  const theme = useTheme();
  const { isPhone } = useResponsive();
  const [activeTab, setActiveTab] = useState(0);
  const profitColor = theme.palette.profitColor ?? "#16a34a";
  const lossColor = theme.palette.lossColor ?? "#dc2626";
  const primary = theme.palette.primary.main;

  const winLossPie = useMemo(() => {
    if (!tradeStatistics) return [];
    const wins = Number(tradeStatistics.profitTrades) || 0;
    const losses = Number(tradeStatistics.lossTrades) || 0;
    if (wins === 0 && losses === 0) {
      // Placeholder slice — its value of 1 is a rendering trick, so don't print it.
      return [
        {
          name: "No closed trades",
          value: 1,
          hideValue: true,
          color: alpha(theme.palette.text.disabled, 0.5),
        },
      ];
    }
    return [
      { name: "Winning trades", value: wins, color: profitColor },
      { name: "Losing trades", value: losses, color: lossColor },
    ].filter((d) => d.value > 0);
  }, [tradeStatistics, profitColor, lossColor, theme.palette.text.disabled]);

  const plBar = useMemo(() => {
    if (!tradeStatistics) return [];
    const p = Math.max(0, Number(tradeStatistics.totalProfit) || 0);
    const l = Math.abs(Number(tradeStatistics.totalLoss) || 0);
    return [
      { name: "Profit", amount: p, fill: profitColor },
      { name: "Loss", amount: l, fill: lossColor },
    ];
  }, [tradeStatistics, profitColor, lossColor]);

  /** Two-line axis tick: the series name, with its amount underneath. */
  const AmountAxisTick = ({ x, y, payload }) => {
    const row = plBar.find((d) => d.name === payload?.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={14} textAnchor="middle" fontSize={12} fontWeight={600} fill={theme.palette.text.secondary}>
          {payload?.value}
        </text>
        <text x={0} y={0} dy={31} textAnchor="middle" fontSize={12} fontWeight={800} fill={row?.fill || theme.palette.text.primary}>
          {inr(row?.amount)}
        </text>
      </g>
    );
  };

  if (!tradeStatistics) return null;

  const pieHeight = isPhone ? 200 : 220;
  const barHeight = isPhone ? 210 : 220;

  const outcomeChart = (
    <ResponsiveContainer width="100%" height={pieHeight}>
      <PieChart>
        <Pie
          data={winLossPie}
          cx="50%"
          cy="50%"
          innerRadius={isPhone ? 40 : 52}
          outerRadius={isPhone ? 66 : 88}
          paddingAngle={3}
          dataKey="value"
          nameKey="name"
        >
          {winLossPie.map((entry, i) => (
            <Cell key={i} fill={entry.color} stroke={theme.palette.background.paper} strokeWidth={2} />
          ))}
        </Pie>
        <Tooltip formatter={(v) => [v, "Trades"]} />
        <Legend verticalAlign="bottom" height={isPhone ? 46 : 28} content={<PieValueLegend data={winLossPie} />} />
      </PieChart>
    </ResponsiveContainer>
  );

  const plChart = (
    <ResponsiveContainer width="100%" height={barHeight}>
      {/* bottom margin 0 + a taller XAxis: the amount line is added inside the axis band
          so the card keeps its original height instead of growing. */}
      <BarChart data={plBar} margin={{ top: 16, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.8)} />
        <XAxis dataKey="name" interval={0} height={42} tickLine={false} tick={<AmountAxisTick />} />
        <YAxis tick={{ fontSize: 11 }} width={isPhone ? 48 : 60} tickFormatter={compactInr} />
        <Tooltip formatter={(v) => [`₹ ${Number(v).toLocaleString()}`, ""]} />
        <Bar dataKey="amount" radius={[8, 8, 0, 0]} maxBarSize={72}>
          {plBar.map((entry, i) => (
            <Cell key={i} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const charts = [
    {
      key: "outcome",
      tab: "Outcome mix",
      icon: PieIcon,
      title: "Trade outcome mix",
      subtitle: "Share of winning vs losing closed trades",
      node: outcomeChart,
    },
    {
      key: "pl",
      tab: "Profit vs loss",
      icon: BarIcon,
      title: "Profit vs loss (amount)",
      subtitle: "Aggregated P/L from this run",
      node: plChart,
    },
  ];

  // Below 480px two stacked charts cost ~580px of scroll for two figures that are also
  // in the stat tiles above, so they share one card behind a tab strip instead.
  if (isPhone) {
    const active = charts[Math.min(activeTab, charts.length - 1)];
    return (
      <Box
        sx={{
          mt: 0.5,
          borderRadius: 3,
          border: `1px solid ${alpha(primary, 0.12)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          overflow: "hidden",
        }}
      >
        <Tabs
          value={Math.min(activeTab, charts.length - 1)}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            borderBottom: `1px solid ${alpha(primary, 0.12)}`,
            "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 700, fontSize: "0.78rem", px: 1 },
          }}
        >
          {charts.map((c) => (
            <Tab key={c.key} label={c.tab} />
          ))}
        </Tabs>
        <Box sx={{ p: 1.25 }}>
          <ChartShell icon={active.icon} title={active.title} subtitle={active.subtitle} framed={false}>
            {active.node}
          </ChartShell>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 1.5,
        mt: 0.5,
      }}
    >
      {charts.map((c) => (
        <ChartShell key={c.key} icon={c.icon} title={c.title} subtitle={c.subtitle}>
          {c.node}
        </ChartShell>
      ))}
    </Box>
  );
};

export default BTCharts;
